import { SectionsTreeNode } from '../types';
import { recalculateNumberOfPages } from './recalculateNumberOfPages';

/**
 * Create a Set from pageIds array for O(1) lookups.
 */
const makeIdsSet = (pageIds: string[]): Set<string> => new Set(pageIds);

/**
 * Remove page ids from a single node and return an updated node.
 * If pageIds becomes empty, it is set to undefined.
 */
const removePageIdsFromNode = (node: SectionsTreeNode, idsToRemove: Set<string>): SectionsTreeNode => {
  let pageIdsUpdated: string[] | undefined = node.pageIds;

  if (pageIdsUpdated && pageIdsUpdated.length > 0) {
    const filtered = pageIdsUpdated.filter((id) => !idsToRemove.has(id));
    pageIdsUpdated = filtered.length > 0 ? filtered : undefined;
  }

  return {
    ...node,
    pageIds: pageIdsUpdated
  };
};

/**
 * Recursively traverse nodes and remove pageIds.
 */
const removeFromNodes = (nodes: SectionsTreeNode[], idsToRemove: Set<string>): SectionsTreeNode[] =>
  nodes.map((node) => {
    const updatedNode = removePageIdsFromNode(node, idsToRemove);
    const children = updatedNode.children ? removeFromNodes(updatedNode.children, idsToRemove) : undefined;
    return {
      ...updatedNode,
      children
    };
  });

interface RemovePageIdsOptions {
  tree: SectionsTreeNode[];
  pageIds: string[];
}

/**
 * Removes specified pageIds from the tree.
 */
export const removePageIdsFromTree = ({ tree, pageIds }: RemovePageIdsOptions): SectionsTreeNode[] => {
  const idsToRemove = makeIdsSet(pageIds);

  const modifiedTree = removeFromNodes(tree, idsToRemove);

  // Recalculate counts and filter out nodes with 0 pages
  const recalculated = recalculateNumberOfPages(modifiedTree);
  return recalculated;
};
